import {
  type NavigateResultPayload,
  type NavigateToNodePayload,
} from "@claudafinil/shared/contract";
import { AnnotationsView } from "../../features/annotations";
import { PromptsView } from "../../features/prompts";
import { TreeView } from "../../features/timeline";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";

type AppTab = "timeline" | "prompts" | "annotations";

const APP_TABS: Array<{ id: AppTab; label: string }> = [
  { id: "timeline", label: "Timeline" },
  { id: "prompts", label: "Prompts" },
  { id: "annotations", label: "Annotations" },
];

interface RemoteTabsProps {
  onNavigateToNode: (targetNodeIdOrText: string) => Promise<NavigateResultPayload>;
  onNavigateToAnnotation: (target: NavigateToNodePayload) => Promise<NavigateResultPayload>;
  onInjectPrompt: (text: string) => Promise<{ success: boolean; error?: string }>;
  onRefreshConversation: () => Promise<{ success: boolean; error?: string }>;
}

export function RemoteTabs({
  onNavigateToNode,
  onNavigateToAnnotation,
  onInjectPrompt,
  onRefreshConversation,
}: Readonly<RemoteTabsProps>): JSX.Element {
  return (
    <Tabs defaultValue="timeline" className="flex min-h-0 flex-1 flex-col">
      <TabsList
        className="flex w-full shrink-0 gap-1 border-b border-border bg-card px-1 sm:px-2"
        aria-label="Drawer views"
      >
        {APP_TABS.map((tab) => (
          <TabsTrigger
            key={tab.id}
            value={tab.id}
            className="min-h-9 flex-1 rounded-none border-0 border-b-2 border-transparent bg-transparent text-xs font-medium text-muted-foreground data-[state=active]:border-b-foreground data-[state=active]:font-semibold data-[state=active]:text-foreground sm:min-h-10 sm:text-[0.8125rem]"
          >
            {tab.label}
          </TabsTrigger>
        ))}
      </TabsList>

      <TabsContent value="timeline" className="m-0 min-h-0 flex-1">
        <TreeView
          onNavigateToNode={onNavigateToNode}
          onRefreshConversation={onRefreshConversation}
        />
      </TabsContent>
      <TabsContent value="prompts" className="m-0 min-h-0 flex-1">
        <PromptsView onInjectPrompt={onInjectPrompt} />
      </TabsContent>
      <TabsContent value="annotations" className="m-0 min-h-0 flex-1">
        <AnnotationsView
          onNavigateToNode={onNavigateToNode}
          onNavigateToAnnotation={onNavigateToAnnotation}
        />
      </TabsContent>
    </Tabs>
  );
}
